import { useEffect, useState } from 'react';

/**
 * useOnlineStatus
 * ------------------------------------------------------------------
 * Hook simple que devuelve `true` cuando el navegador detecta red, y
 * `false` cuando está offline. Lo usamos para:
 *  - Mostrar el banner "Sin conexión" en el Topbar.
 *  - Bloquear / advertir en flujos críticos (POS, checkout, login).
 *  - Permitir que componentes reaccionen sin propagar booleanos manualmente.
 *
 * Es suficiente para la mayoría de los casos. Si necesitamos certeza real
 * (por ejemplo, "hay red pero el server no responde"), conviene además
 * envolver supabase.from(...) con un timeout y caer al cache del SW.
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(() => {
    if (typeof navigator === 'undefined') return true;
    return navigator.onLine !== false;
  });

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

export default useOnlineStatus;
